import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';

export const NewPlaylistDialog = ({ open, playlists, onClose, onCreate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const MAX_LENGTH = 70;

  const handleChange = (e) => {
    const { value } = e.target;

    if (value.length > MAX_LENGTH) {
      setError('We\'re sorry, but the playlist name you chose is too long.');
      return; // block input
    }

    setTitle(value);
    setError('');
  };

  const handleSubmit = () => {
    const trimmed = title.trim();

    if (!trimmed || error) return;

    if (playlists?.some((p) => p.title.trim() === trimmed)) {
      setError('This playlist name already exists. Please enter a different name.');
      return;
    }

    onCreate(trimmed);
    setTitle('');
    setError('');
  };

  const handleClose = () => {
    setTitle('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        New Playlist
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          autoFocus
          margin="dense"
          label="Playlist Title"
          value={title}
          onChange={handleChange}
          error={!!error}
          helperText={error}
        />
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
          {title.length}
          {' '}
          /
          {MAX_LENGTH}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!title.trim() || !!error}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewPlaylistDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  playlists: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
};
